import { Add as AddIcon, Edit as EditIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";

import ContractorTeamApi from "../../../../infrastructure/api/contractorTeamApi";
import CustomerRequestLocationService from "../../../../infrastructure/api/customerRequestLocation";
import RoleGuardRoute from "../../../components/layout/RoleGuardRoute";
import InviteContractorMemberDialog from "./components/InviteContractorMemberDialog";

const ContractorTeamPageContent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [context, setContext] = useState(null);
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [contextRes, teamRes, locationData] = await Promise.all([
        ContractorTeamApi.getContext(),
        ContractorTeamApi.getMembers(),
        CustomerRequestLocationService.loadCustomerRequestLocationLists(),
      ]);

      if (!contextRes.isSuccess) {
        setError(contextRes.message);
        return;
      }
      if (!teamRes.isSuccess) {
        setError(teamRes.message);
        return;
      }

      setContext(contextRes.data);
      setItems(Array.isArray(teamRes.data) ? teamRes.data : []);
      setCities(locationData?.cities || []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (payload) => {
    setSaving(true);
    try {
      const res = await ContractorTeamApi.inviteMember(payload);
      if (!res.isSuccess) return { error: res.message };
      toast.success(res.message || "کارشناس دعوت شد.");
      setInviteOpen(false);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!editMember) return {};
    setSaving(true);
    try {
      const memberId = editMember.memberId ?? editMember.MemberId;
      const res = await ContractorTeamApi.updateMember(memberId, payload);
      if (!res.isSuccess) return { error: res.message };
      toast.success("عضو تیم به‌روزرسانی شد.");
      setEditMember(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>در حال بارگذاری...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!context?.canManageTeam && !context?.CanManageTeam) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">مدیریت تیم فقط برای حساب اصلی پیمانکار فعال است.</Alert>
      </Box>
    );
  }

  const companyName = context?.companyName ?? context?.CompanyName ?? "پیمانکار";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        تیم کارشناسان
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {companyName} — دعوت کارشناسان برای ثبت متقاضی و محدودسازی دسترسی شهرستان
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setInviteOpen(true)}>
          دعوت کارشناس
        </Button>
        <Button component={RouterLink} to="/SLN/Contractor/AssistedRegistration" variant="outlined">
          بازگشت به ثبت متقاضی
        </Button>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card variant="outlined">
        <CardContent sx={{ overflowX: "auto", p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>نام</TableCell>
                <TableCell>موبایل</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>ثبت متقاضی</TableCell>
                <TableCell>شهرهای مجاز</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    هنوز کارشناسی دعوت نشده است.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => {
                  const cityIds = row.allowedCityIds ?? row.AllowedCityIds ?? [];
                  return (
                    <TableRow key={row.memberId ?? row.MemberId} hover>
                      <TableCell>{row.displayName ?? row.DisplayName ?? "—"}</TableCell>
                      <TableCell>{row.mobile ?? row.Mobile ?? "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.isActive ?? row.IsActive ? "success" : "default"}
                          label={row.isActive ?? row.IsActive ? "فعال" : "غیرفعال"}
                        />
                      </TableCell>
                      <TableCell>
                        {row.canAssistedRegistration ?? row.CanAssistedRegistration
                          ? "بله"
                          : "خیر"}
                      </TableCell>
                      <TableCell>
                        {cityIds.length === 0 ? "همه شهرها" : `${cityIds.length} شهر`}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => setEditMember(row)}
                          aria-label="ویرایش"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InviteContractorMemberDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
        saving={saving}
        cities={cities}
      />

      <InviteContractorMemberDialog
        open={Boolean(editMember)}
        onClose={() => setEditMember(null)}
        onSubmit={handleUpdate}
        saving={saving}
        cities={cities}
        editMember={editMember}
      />
    </Box>
  );
};

const ContractorTeamPage = () => (
  <RoleGuardRoute requireContractorPrimary>
    <ContractorTeamPageContent />
  </RoleGuardRoute>
);

export default ContractorTeamPage;
